import PropTypes from 'prop-types';
import { Button, Typography } from 'antd';
import { IconError, IconReload } from '../icons';
import { useTranslation } from 'react-i18next';
import IconTile from './IconTile';
import './primitives.css';

const { Title, Text } = Typography;

/**
 * ErrorState
 *
 * A page-level failure with one recovery action. It shows a localised sentence
 * and a retry — never what the server said.
 *
 * That last part is enforced here rather than trusted to callers, because the
 * shape of the mistake is always the same: `description={err.response.data.message}`
 * is one keystroke away, it works in development, and what it eventually puts in
 * front of a centre's receptionist is a Java package name — or a database
 * constraint, which leaks the schema. Anything that smells like a server string
 * is dropped and, in development, logged.
 */

/* Deliberately conservative: it only has to catch what a server actually sends.
   A localised sentence never matches any of these. */
const SERVER_STRING_PATTERNS = [
  /\b(?:java|javax|jakarta|org|com|net|io)\.[a-z]+\.[A-Za-z]/, // package / class path
  /\b(?:Exception|Throwable|Error:|Caused by|stack ?trace)\b/i,
  /\bat\s+[\w$.]+\([\w$.]+:\d+\)/, // stack frame
  /^\s*[[{<]/, // a JSON or XML payload rendered as text
  /\b(?:SQL|JDBC|constraint|violat(?:es|ion)|null ?pointer|undefined is not|NaN)\b/i,
  /\b(?:ERR_[A-Z_]+|ECONNREFUSED|ETIMEDOUT|Network ?Error)\b/i,
  /\bRequest failed with status\b|\bstatus code \d{3}\b|\bHTTP \d{3}\b/i,
  /\n/, // multi-line output is never copy
];

/** Long enough for any real sentence in uz, ru or en; short of a payload. */
const MAX_DESCRIPTION_LENGTH = 200;

export const looksLikeServerString = (text) =>
  text.length > MAX_DESCRIPTION_LENGTH ||
  SERVER_STRING_PATTERNS.some((pattern) => pattern.test(text));

const safeDescription = (description) => {
  if (typeof description !== 'string') return description; // a node is authored copy
  const text = description.trim();
  if (!text) return null;
  if (!looksLikeServerString(text)) return text;
  if (process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line no-console
    console.warn('ErrorState: dropped a raw server string. Pass a localised sentence.', text);
  }
  return null;
};

const ErrorState = ({
  title,
  description,
  onRetry,
  retryLabel,
  icon = <IconError />,
  className = '',
}) => {
  const { t } = useTranslation();
  const shown = safeDescription(description);

  return (
    <div
      className={['lms-error-state', className].filter(Boolean).join(' ')}
      role="alert"
      aria-live="assertive"
    >
      <IconTile icon={icon} tone="danger" size="lg" className="lms-error-state__icon" />
      <Title level={4} className="lms-error-state__title">
        {title || t('errors.somethingWentWrong')}
      </Title>
      {shown && (
        <Text type="secondary" className="lms-error-state__description">
          {shown}
        </Text>
      )}
      {onRetry && (
        <Button
          type="primary"
          icon={<IconReload />}
          onClick={onRetry}
          className="lms-error-state__action"
        >
          {retryLabel || t('common.retry')}
        </Button>
      )}
    </div>
  );
};

ErrorState.propTypes = {
  title: PropTypes.node,
  /** A localised sentence. A raw server string is dropped, not printed. */
  description: PropTypes.node,
  onRetry: PropTypes.func,
  retryLabel: PropTypes.node,
  icon: PropTypes.node,
  className: PropTypes.string,
};

export default ErrorState;
